// [Codex 2026-07-16] S139: normal durable chat errors remain in the chat surface.
// [claude-code 2026-03-16] Bridges apiClient error bus → Toast notifications
import { useEffect, useRef } from "react";
import { useToast } from "../contexts/ToastContext";
import {
  onApiError,
  onApiRecovery,
  getFixDescription,
  type ApiErrorEvent,
} from "../lib/errorBus";
import { BACKEND_MAINTENANCE_MESSAGE } from "../lib/maintenance-message";
import { isWeb } from "../lib/platform";

/** Minimum ms between toasts for the same error code to prevent spam */
const DEDUP_WINDOW_MS = 5000;
const INLINE_CHAT_ENDPOINT_PREFIXES = ["/api/chat/runs"];

export function ApiErrorToastBridge() {
  const { addToast } = useToast();
  const recent = useRef<Map<string, number>>(new Map());
  const outageEndpoint = useRef<string | null>(null);

  useEffect(() => {
    if (!isWeb()) return;

    const stopRecovery = onApiRecovery((endpoint) => {
      if (endpoint !== outageEndpoint.current) return;
      outageEndpoint.current = null;
      recent.current.clear();
    });
    const stopErrors = onApiError((err: ApiErrorEvent) => {
      if (
        err.endpoint &&
        INLINE_CHAT_ENDPOINT_PREFIXES.some((endpoint) =>
          err.endpoint?.startsWith(endpoint),
        )
      ) {
        return;
      }

      if (isBackendOutage(err)) {
        if (outageEndpoint.current) return;
        outageEndpoint.current = err.endpoint ?? "__network__";
      }

      // Deduplicate by error class so one outage cannot create one toast per endpoint.
      const key = `${err.code}:${err.status ?? "none"}`;
      const now = Date.now();
      const last = recent.current.get(key);
      if (last && now - last < DEDUP_WINDOW_MS) return;
      recent.current.set(key, now);

      // Clean old entries periodically
      if (recent.current.size > 50) {
        for (const [k, t] of recent.current) {
          if (now - t > DEDUP_WINDOW_MS) recent.current.delete(k);
        }
      }

      const fix = getFixDescription(err.code, err.status);
      const message = err.message || BACKEND_MAINTENANCE_MESSAGE;
      const description =
        fix === BACKEND_MAINTENANCE_MESSAGE || fix === message
          ? undefined
          : fix;
      addToast(message, "error", description, "api-error");
    });

    return () => {
      stopErrors();
      stopRecovery();
    };
  }, [addToast]);

  return null;
}

function isBackendOutage(error: ApiErrorEvent): boolean {
  return (
    error.status === undefined ||
    error.status >= 500 ||
    error.code === "network_error" ||
    error.code === "resource_exhausted" ||
    error.code === "server_error"
  );
}
